import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { 
  CheckCircle, 
  Loader2, 
  XCircle,
  Crown,
  Flame,
  ArrowRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SubscribeSuccess = () => {
  const { language } = useLanguage();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [paymentData, setPaymentData] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const pollPaymentStatus = async () => {
      try {
        const response = await axios.get(`${API}/stripe/status/${sessionId}`);
        const data = response.data;

        setPaymentData(data);

        if (data.payment_status === 'paid') {
          setStatus('success');
          return true;
        } else if (data.status === 'expired') {
          setStatus('error');
          return true;
        }

        return false;
      } catch (error) {
        console.error('Error checking payment status:', error);
        if (pollCount >= 4) {
          setStatus('error');
          return true;
        }
        return false;
      }
    };

    // Initial check
    pollPaymentStatus().then((done) => {
      if (!done && pollCount < 5) {
        // Poll every 2 seconds, up to 5 times
        const interval = setInterval(async () => {
          setPollCount((prev) => prev + 1);
          const done = await pollPaymentStatus();
          if (done || pollCount >= 4) {
            clearInterval(interval);
          }
        }, 2000);

        return () => clearInterval(interval);
      }
    });
  }, [sessionId, pollCount]);

  const getTierIcon = (tier) => {
    if (tier === 'expert') return Flame;
    return Crown;
  };

  const getTierColor = (tier) => {
    if (tier === 'expert') return 'text-orange-400';
    return 'text-primary';
  };

  if (status === 'loading') {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-card border-white/10">
          <CardContent className="p-8 text-center">
            <Loader2 className="w-16 h-16 animate-spin text-primary mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">
              {language === 'fr' ? 'Vérification du paiement...' : 'Verifying payment...'}
            </h2>
            <p className="text-muted-foreground">
              {language === 'fr' 
                ? 'Veuillez patienter quelques instants' 
                : 'Please wait a moment'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-card border-red-500/30">
          <CardContent className="p-8 text-center">
            <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2 text-red-400">
              {language === 'fr' ? 'Erreur de paiement' : 'Payment Error'}
            </h2>
            <p className="text-muted-foreground mb-6">
              {language === 'fr' 
                ? 'Une erreur est survenue lors de la vérification du paiement.' 
                : 'An error occurred while verifying the payment.'}
            </p>
            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={() => navigate('/subscribe')}>
                {language === 'fr' ? 'Réessayer' : 'Try Again'}
              </Button>
              <Button variant="outline" onClick={() => navigate('/dashboard')}>
                {language === 'fr' ? 'Tableau de bord' : 'Dashboard'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  const TierIcon = paymentData?.tier ? getTierIcon(paymentData.tier) : Crown;
  const tierColor = paymentData?.tier ? getTierColor(paymentData.tier) : 'text-primary';

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-card border-primary/30">
        <CardContent className="p-8 text-center">
          <div className="relative mb-6">
            <CheckCircle className="w-20 h-20 text-green-400 mx-auto" />
            <div className={`absolute -bottom-2 -right-2 w-10 h-10 rounded-full bg-card border-2 border-primary flex items-center justify-center`}>
              <TierIcon className={`w-5 h-5 ${tierColor}`} />
            </div>
          </div>
          
          <h2 className="text-2xl font-bold mb-2">
            {language === 'fr' ? 'Paiement réussi !' : 'Payment Successful!'}
          </h2>
          
          <p className="text-muted-foreground mb-2">
            {language === 'fr' 
              ? 'Bienvenue dans TRADEX AI' 
              : 'Welcome to TRADEX AI'}
          </p>
          
          {paymentData?.tier && (
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 ${tierColor} font-medium mb-6`}>
              <TierIcon className="w-4 h-4" />
              {paymentData.tier.charAt(0).toUpperCase() + paymentData.tier.slice(1)}
            </div>
          )}
          
          <div className="bg-secondary/30 rounded-lg p-4 mb-6">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">
                  {language === 'fr' ? 'Montant' : 'Amount'}
                </p>
                <p className="font-bold text-lg">
                  ${paymentData?.amount?.toFixed(2) || '0.00'}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">
                  {language === 'fr' ? 'Statut' : 'Status'}
                </p>
                <p className="font-bold text-lg text-green-400">
                  {language === 'fr' ? 'Payé' : 'Paid'}
                </p>
              </div>
            </div>
          </div>
          
          <div className="space-y-3">
            <Button 
              className="w-full" 
              onClick={() => navigate('/dashboard')}
              data-testid="go-to-dashboard-btn"
            >
              {language === 'fr' ? 'Accéder au tableau de bord' : 'Go to Dashboard'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => navigate('/scan-auto')}
            >
              {language === 'fr' ? 'Configurer Auto-Scan' : 'Configure Auto-Scan'}
            </Button>
          </div>
          
          <p className="text-xs text-muted-foreground mt-6">
            {language === 'fr' 
              ? 'Un email de confirmation vous a été envoyé.' 
              : 'A confirmation email has been sent to you.'}
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscribeSuccess;
